import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { NoticeIcon } from '../../components/icon/icon';
import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../components/modal/confirm.modal';
import { AnimatePresence } from 'framer-motion';
import {
  cancelOrder,
  fetchDriverOrderDetail,
  fetchOrderDetail,
  getObtainsStateList,
  getObtainsVehicle,
  postObtainsOrder,
} from '../../api/order';
import { AlertModal } from '../../components/modal/alert.modal';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authState } from '../../atoms/authState';
import { fetchDriverMypage } from '../../api/mypage';
import { formatNumber } from '../../utils/util';
import { OrderConfirmModal } from '../../components/modal/custom.order.comfirm.modal';
import { orderIdState } from '../../atoms/orderState';
import { vehicleState } from '../../atoms/vehicleState';

interface StandardTaskResponse {
  halfDay: string;
  fullDay: string;
  etcCondition: string;
  otCondition: string;
  cycleCondition: string;
  fuelSupport?: boolean;
}

interface OrderDetailData {
  id: number; // ID는 number 타입으로 수정
  name: string;
  wanted: string;
  targetDate: string;
  targetTime: string;
  work: string;
  amount: number;
  totalVehicle: number;
  remainingVehicle: number;
  notice: string;
  standardTask: StandardTaskResponse; // StandardTask를 객체 타입으로 변경
}

interface DriverMypageResponse {
  vehicle: string;
  phone: string;
  registration: string;
  to_user_id: string;
}

interface VehicleDataResponse {
  vehicle: string[]; // 차량 번호 목록
  orderId: string; // 발주 아이디
}

// API로부터 반환되는 각 수주 내역의 타입 정의
interface ObtainStateListResponse {
  orderId: number; // 발주 아이디
  obtainId: number; // 수주 아이디
  target: string; // 도착 시간
  vehicle: string; // 차량 번호
  obtainCreatedAt: string; // 수주 시간
}

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>(); // URL에서 orderId를 가져옴
  const history = useHistory();
  const auth = useRecoilValue(authState);
  const vehicle = useRecoilValue(vehicleState);
  const setOrderId = useSetRecoilState(orderIdState); // Recoil 상태 업데이트 함수

  const [data, setData] = useState<OrderDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false); // 발주 취소 상태 관리
  const [profileData, setProfileData] = useState<DriverMypageResponse | null>(
    null,
  ); // 드라이버 프로필 데이터를 저장할 상태

  // 모달
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const [confirmModal, setConfirmModal] = useState(false);
  const [orderConfirmModal, setOrderConfirmModal] = useState(false);
  const [obtainStateList, setObtainStateList] = useState<
    ObtainStateListResponse[]
  >([]);

  const [vehicleData, setVehicleData] = useState<VehicleDataResponse>({
    vehicle: [],
    orderId: '',
  });

  // 일치하지 않는 차량을 저장할 상태
  const [remainingVehicles, setRemainingVehicles] = useState<string[]>([]);

  useEffect(() => {
    // obtainStateList와 vehicleData.vehicle 비교
    if (vehicleData.vehicle.length > 0) {
      const unmatchedVehicles = vehicleData.vehicle.filter(
        (vehicle) =>
          !obtainStateList.some((obtain) => obtain.vehicle === vehicle),
      );
      setRemainingVehicles(unmatchedVehicles);
    }
  }, [obtainStateList, vehicleData]);

  const isVehiclesInObtainStateList = () => {
    // vehicleData.vehicle 배열이 비어 있는지 확인
    if (vehicleData.vehicle.length === 0) {
      // obtainStateList와 recoil 상태의 vehicle을 비교
      return obtainStateList.some((obtain) => obtain.vehicle === vehicle);
    }

    // 기존의 로직을 그대로 유지
    return vehicleData.vehicle.every((vehicle) =>
      obtainStateList.some((obtain) => obtain.vehicle === vehicle),
    );
  };

  // 수주 하기 버튼 클릭 시 남은 대수를 다시 가져와서 버튼 클릭을 불가능하게 막아야 함.
  const handleConfirmClick = () => {
    if (vehicleData.vehicle.length > 0) {
      setOrderConfirmModal(true);
    } else {
      setConfirmModal(true);
    }
  };
  const handleConfirmClose = () => {
    setConfirmModal(false);
  };

  // 발주 수정 함수
  const handleModifyOrder = () => {
    history.push({
      pathname: `/managerOrder/update/${id}`,
    });
  };

  // 발주 취소 함수
  const handleCancelOrder = async () => {
    setIsCancelling(true);
    try {
      const response = await cancelOrder(Number(id));
      setModalMessage(response.message);
      setIsModalOpen(true);
      setNavigateOnClose(true);
    } catch (error) {
      console.error('Error cancelling order:', error);
      setModalMessage('발주 취소에 실패했습니다.');
      setIsModalOpen(true);
      setNavigateOnClose(false);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleGoStatusClick = () => {
    setOrderId(Number(id));
    history.push({
      pathname: '/manager/order/status',
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/managerOrder',
      });
    }
  };

  const handelOrderModalClose = () => {
    setOrderConfirmModal(false);
  };

  const orderObtained = async () => {
    try {
      const orderData = {
        vehicle: [],
      };
      const response = await postObtainsOrder(Number(id), orderData);

      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else if (response.statusCode === 400) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setConfirmModal(false);
      } else {
        setModalMessage('수주에 실패했습니다.');
        setIsModalOpen(true);
        setConfirmModal(false);
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      // 오류 처리 로직 추가 가능
    }

    history.push('/userOrder');
  };

  // 데이터 가져오기
  useEffect(() => {
    const getOrderDetail = async () => {
      try {
        let response;
        if (auth.userType === 'company') {
          response = await fetchOrderDetail(Number(id)); // 회사일 경우 API 호출
        } else if (auth.userType === 'driver') {
          response = await fetchDriverOrderDetail(Number(id)); // 운전자일 경우 API 호출

          // 드라이버 데이터 추가로 가져오기
          const driverData = await fetchDriverMypage();
          setProfileData(driverData); // 드라이버 프로필 데이터 상태 업데이트
        }
        setData(response); // 상태 업데이트
      } catch (error) {
        console.error('Error fetching order detail:', error);
      } finally {
        setLoading(false); // 로딩 상태 해제
      }
    };

    getOrderDetail(); // 비동기 함수 호출
  }, [id, auth.userType]); // id와 auth.userType이 변경될 때마다 호출

  useEffect(() => {
    const getVehicle = async () => {
      try {
        const response = await getObtainsVehicle(Number(id));
        setVehicleData(response); //
      } catch (error) {}
    };
    getVehicle();
  }, [id]);

  // 수주 되었는지 확인
  useEffect(() => {
    const getObtainsStatement = async () => {
      try {
        const response = await getObtainsStateList(Number(id));
        setObtainStateList(response);
      } catch (error) {}
    };

    getObtainsStatement();
  }, [id]);

  if (loading) return <p>Loading...</p>;
  if (!data) return <p>No data available.</p>;

  // 오늘 날짜와 arrivalDate 비교
  const today = new Date();
  const arrivalDate = new Date(data.targetDate);
  return (
    <Container>
      <ul>
        <li>
          <div className="label">회사명</div>
          <div className="value">{data?.name}</div>
        </li>
        <li>
          <div className="label">수배 차량</div>
          <div className="value">{data?.wanted}</div>
        </li>
        <li>
          <div className="label">도착 일자</div>
          <div className="value">{data?.targetDate}</div>
        </li>
        <li>
          <div className="label">도착 시간</div>
          <div className="value">{data?.targetTime}</div>
        </li>
        <li>
          <div className="label">작업 형태</div>
          <div className="value">{data?.work === 'full' ? '전일' : '반일'}</div>
        </li>
        <li>
          <div className="label">금액</div>
          <div className="value">{formatNumber(data?.amount)}</div>
        </li>
        {auth.userType === 'driver' ? (
          <li>
            <div className="label">유루 지원</div>
            <div className="value status">
              {data?.standardTask.fuelSupport ? '지원' : '미지원'}
            </div>
          </li>
        ) : null}
        <li>
          <ul>
            <li>
              <span className="label">총 대수</span>
              <span className="value">{data?.totalVehicle}</span>
            </li>
            <li>
              <span className="label">남은 대수</span>
              <RemainColor
                remainNum={parseInt(data?.remainingVehicle?.toString() || '0')}
                className="value"
              >
                {data?.remainingVehicle}
              </RemainColor>
            </li>
          </ul>
        </li>
      </ul>
      <div className="infomation-txt">
        <div className="icon-box">
          <NoticeIcon />
        </div>
        <span>
          <span className="title">공지사항</span>
          <span className="txt">{data?.notice}</span>
        </span>
      </div>
      <div className="work-standard">
        <div className="label">작업 기준</div>
        {data?.standardTask ? (
          <>
            {data.standardTask.fullDay !== '0' ||
            data.standardTask.halfDay !== '0' ||
            data.standardTask.etcCondition !== '0' ||
            data.standardTask.otCondition !== '0' ||
            data.standardTask.cycleCondition !== '0' ? (
              <>
                {data?.standardTask.fullDay &&
                data.standardTask.fullDay !== '0' ? (
                  <div className="value">{data.standardTask.fullDay}</div>
                ) : null}
                {data?.standardTask.halfDay &&
                data.standardTask.halfDay !== '0' ? (
                  <div className="value">{data.standardTask.halfDay}</div>
                ) : null}
                {data?.standardTask.etcCondition &&
                data.standardTask.etcCondition !== '0' ? (
                  <div className="value">{data.standardTask.etcCondition}</div>
                ) : null}
                {data?.standardTask.otCondition &&
                data.standardTask.otCondition !== '0' ? (
                  <div className="value">{data.standardTask.otCondition}</div>
                ) : null}
                {data?.standardTask.cycleCondition &&
                data.standardTask.cycleCondition !== '0' ? (
                  <div className="value">
                    {data.standardTask.cycleCondition}
                  </div>
                ) : null}
              </>
            ) : (
              <span>작업 기준이 없습니다. 작업 기준을 업데이트 하세요.</span>
            )}
          </>
        ) : (
          <span>작업 기준이 없습니다. 작업 기준을 업데이트 하세요.</span>
        )}
      </div>
      {auth.userType === 'company' && arrivalDate >= today ? (
        <div className="order-cancle-btn-box">
          <button className="update-btn" onClick={handleModifyOrder}>
            수정
          </button>
          <button
            onClick={handleCancelOrder}
            disabled={isCancelling}
            className="cancle-btn"
          >
            {isCancelling ? '취소 중...' : '발주 취소'}
          </button>
        </div>
      ) : null}
      {auth.userType === 'driver' ? (
        profileData?.to_user_id === '' ? (
          data.remainingVehicle > 0 ? (
            <ConfirmBtn
              onClick={handleConfirmClick}
              disabled={isVehiclesInObtainStateList()}
            >
              수주 하기
            </ConfirmBtn>
          ) : (
            <ConfirmBtn onClick={handleConfirmClick} disabled>
              남은 대수 없음
            </ConfirmBtn>
          )
        ) : (
          <ConfirmBtn onClick={handleConfirmClick} disabled>
            수주 권한 없음 (권한 위임 중)
          </ConfirmBtn>
        )
      ) : (
        <ConfirmBtn onClick={handleGoStatusClick}>수주 현황</ConfirmBtn>
      )}
      <AnimatePresence initial={false}>
        {confirmModal ? (
          <ConfirmModal
            handleConfirmClose={handleConfirmClose}
            confirmText="해당 건을 수주 하시겠습니까?"
            onClick={orderObtained}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {isModalOpen && (
          <AlertModal
            handleConfirmClose={handleModalClose}
            confirmText={modalMessage}
            onClick={handleModalClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {orderConfirmModal && (
          <OrderConfirmModal
            handleConfirmClose={handelOrderModalClose}
            onClick={handelOrderModalClose}
            id={Number(id)}
            remainingVehicleLength={Number(data.remainingVehicle)}
            remainingVehicles={remainingVehicles}
            setConfirmModal={setConfirmModal}
          />
        )}
      </AnimatePresence>
    </Container>
  );
};

export default OrderDetail;

// style
const Container = styled.div`
  padding: 0 1.5rem;
  > ul {
    margin-top: 0.6rem;
    padding: 2.4rem 2.4rem 9.2rem;
    border-radius: 1.6rem;
    box-shadow: 0 2px 5px 3px rgba(0, 0, 0, 0.05);
    position: relative;
    background-color: ${(props) => props.theme.textWhiteColor};
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    > li:not(:last-child) {
      display: flex;
      gap: 2.4rem;
      align-items: center;
      .label {
        min-width: 5.4rem;
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.textAAAColor};
      }
      .value {
        font-size: 1.4rem;
        font-weight: 600;
        &.status {
          min-width: 4.2rem;
          color: ${(props) => props.theme.textWhiteColor};
          background-color: ${(props) => props.theme.btnBlueColor};
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.4rem;
          font-size: 1.1rem;
          border-radius: 1.1rem;
        }
      }
    }
    > li:last-child {
      position: absolute;
      bottom: 0;
      left: 0;
      padding: 1.9rem 1.5rem;
      width: 100%;
      ul {
        display: flex;
        padding: 0.4rem 0;
        background-color: ${(props) => props.theme.homeMainBoxBgColor};
        border-radius: 0.8rem;
        overflow: hidden;
        li {
          padding: 1.6rem 1.6rem 1.7rem;
          color: ${(props) => props.theme.textWhiteColor};
          flex: 1 1 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          .label {
            color: rgba(255, 255, 255, 0.6);
            font-size: 1.2rem;
          }
          .value {
            font-size: 1.4rem;
          }
          &:not(:first-child) {
            border-left: 1px solid rgba(255, 255, 255, 0.3);
            .label {
              color: rgba(255, 255, 255, 0.8);
            }
            .value {
              color: ${(props) => props.theme.textWhiteColor};
              min-width: 3rem;
              padding: 0.4rem 0.3rem 0.3rem;
              background-color: ${(props) => props.theme.btnBlueColor};
              border-radius: 1.1rem;
              display: flex;
              justify-content: center;
              align-items: center;
              font-size: 1.2rem;
            }
          }
        }
      }
    }
  }
  .infomation-txt {
    margin-top: 2.4rem;
    display: flex;
    padding: 1.6rem;
    background-color: rgba(67, 123, 230, 0.12);
    border-radius: 0.8rem;
    align-items: center;
    gap: 1.6rem;
    .icon-box {
      display: flex;
    }
    > span {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      .title {
        font-size: 1.4rem;
        font-weight: 600;
      }
      .txt {
        font-size: 1.3rem;
        font-weight: 300;
      }
    }
  }
  .work-standard {
    display: flex;
    flex-direction: column;
    margin-top: 2.4rem;
    gap: 1rem;
    .label {
      font-size: 1.6rem;
      font-weight: 600;
    }
    .value {
      font-size: 1.3rem;
      font-weight: 300;
    }
    span {
      font-size: 1.3rem;
      font-weight: 300;
    }
  }
  .order-cancle-btn-box {
    position: absolute;
    bottom: 9.4rem;
    left: 0;
    width: 100%;
    padding: 1rem 1.5rem 2.4rem;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 0.8rem;
    button {
      width: 100%;
      padding: 0.8rem;
      border: none;
      outline: none;
      border-radius: 0.8rem;
    }
    button.update-btn {
      background-color: ${(props) => props.theme.btnBlueColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    button.cancle-btn {
      background-color: ${(props) => props.theme.errorColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
  }
`;

const ConfirmBtn = styled.button<{ disabled?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) =>
    props.disabled ? props.theme.gray700 : props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;

const RemainColor = styled.span<{ remainNum: number }>`
  background-color: ${(props) =>
    props.remainNum > 2
      ? props.theme.btnBlueColor
      : props.theme.redColor} !important;
`;
